import React, { useEffect, useState } from 'lib/react';
import { EVButton, makeStyles, EVUploadIcon } from 'lib/components';
import PropTypes from 'prop-types';
import { useTranslation } from 'lib/translation';
import {
  CRYPTO_KEY, CRYPTO_IV, LEGACY_PORTAL_LINKS, HOST_URL,
} from 'constants/AppConstants';
import { UPLOAD_INFORMATION_ROUTE } from 'constants/RouteConstants';
import { Link, useLocation } from 'lib/router';
import { encryptAndEncode } from 'utils/app.utils';
import { useSelector } from 'lib/redux';
import { featureFlagSelector, isRedirectExceptionOrgSelector } from 'utils/appSelectors.utils';
import * as featureFlags from 'constants/FeatureFlag.constants';
import getReportDetailsService from 'services/getReportDetails.service';

export const useStyles = makeStyles((theme) => ({
  uploadBtn: {
    width: '125px',
    backgroundColor: theme.evPalette.salt[100],
    borderColor: theme.evPalette.charcoal[300],
    padding: '4px 12px 4px 12px',
  },
  uploadBtnLabel: {
    columnGap: '8px',
    padding: 0,
    alignItems: 'center',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: '19px',
    textTransform: 'none',
  },
}));

function UploadButton({ reportId, signedReportId }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const [encryptedReportID, setEncryptedReportID] = useState(null);
  const [missingInformation, setMissingInformation] = useState('');

  const location = useLocation();
  const currentUrl = `${location.pathname}${location.search}`;

  const myevRefreshLegacyRedirectFlag = useSelector(featureFlagSelector(featureFlags.REDIRECT_ACTION_BUTTONS_TO_LEGACY_PORTAL));// && false;
  const isRedirectExceptionOrg = useSelector(isRedirectExceptionOrgSelector);

  useEffect(async () => {
    let isMounted = true;

    const encryptedID = await encryptAndEncode(reportId, CRYPTO_KEY, CRYPTO_IV);

    const reportDetailsResponse = myevRefreshLegacyRedirectFlag
      ? await getReportDetailsService(reportId, signedReportId)
      : { json: {} };

    const { json: reportDetails } = reportDetailsResponse;

    if (isMounted) {
      setEncryptedReportID(encryptedID);
      if (isMounted && reportDetails?.missingInformation) {
        setMissingInformation(reportDetails.missingInformation);
      }
    }

    return () => { isMounted = false; };
  }, []);

  const uploadButtonProps = (() => {
    if (myevRefreshLegacyRedirectFlag) {
      return ({
        href: `${LEGACY_PORTAL_LINKS.upload + encryptedReportID}&missinginfo=${missingInformation}`,
        target: '_blank',
      });
    }

    const signatureQuery = signedReportId ? `&signature=${encodeURIComponent(signedReportId)}` : ''; // #genai

    if (isRedirectExceptionOrg) {
      return ({
        component: Link,
        to: `${UPLOAD_INFORMATION_ROUTE}?id=${encryptedReportID}${signatureQuery}`,
        state: { currentUrl },
      });
    }

    return ({
      href: `${HOST_URL}${UPLOAD_INFORMATION_ROUTE}?id=${encryptedReportID}${signatureQuery}`,
      target: '_blank',
    });
  })();

  return (
    <EVButton
      id={`uploadBtn-${reportId}`}
      variant="outlined"
      color="primary"
      className={classes.uploadBtn}
      classes={{ label: classes.uploadBtnLabel }}
      onClick={(e) => e.stopPropagation()}
      {...uploadButtonProps}
    >
      <EVUploadIcon />
      {t('common_upload')}
    </EVButton>
  );
}

UploadButton.propTypes = {
  reportId: PropTypes.number.isRequired,
  signedReportId: PropTypes.string,
};

UploadButton.defaultProps = {
  signedReportId: null,
};

export default UploadButton;
